// What a floor card says about itself. Adapted from the Andon app's
// `lib/floor.ts`.
//
// Pulled out of the card because none of it is rendering. What the chip on a
// card's face says and which clock it shows are decisions the Andon tab and
// the thread's header have to make IDENTICALLY — they draw the same andon from
// the same payload.
//
// WHAT DID NOT COME ACROSS. The Andon app's Floor ranked, searched and counted
// a whole shift from here (`byAge`, `searchAlerts`, `counts`), and read a T
// tick for follow-up tasks off every card. The shop floor's Andon tab groups
// by stage and sorts the way the server returns, the follow-ups were dropped
// in the merge, and a search over the floor is the Pods finder's job — so
// those stayed behind. What survives is the part that was never a property of
// a list: a question only you can answer, said on one card's face, and the
// clock beside it.

import type { AndonAlertVM } from '@/api/andon/types';
import type { StringKey } from '@/i18n/strings';
import { ago, clock } from '@/lib/time';

/**
 * What THIS person owes on this card, said on its face.
 *
 * The rule for both: a question one tap inside a list is a question nobody
 * answers. Both are dead ends for everybody but the person who raised it —
 * the stop estimate nobody else can see from where they are standing, and
 * the moment that estimate arrives.
 */
export type Owed = 'confirmStop' | 'estimateStop' | null;

export function owes(a: AndonAlertVM): Owed {
  if (!a.open) return null;
  if (!a.raisedByMe) return null;
  if (a.stopForecast?.due) return 'confirmStop';
  if (a.needsStopEstimate) return 'estimateStop';
  return null;
}

/**
 * The clock, as the card shows it: `−0:47`, `7:41`, `1:12:04 ago`.
 *
 * TWO DIFFERENT CLOCKS, AND THE CARD SAYS WHICH.
 *
 *   a countdown   time left before this breaches. Bare, and it changes
 *                 colour as it runs out: ink, then amber, then red with a
 *                 minus sign once it has gone.
 *   an age        how long since it was raised. Suffixed `ago`, and always
 *                 quiet, because nothing is running out.
 *
 * THE AGE IS THE PART THAT WAS MISSING. Once somebody is engaged the ladder
 * stops climbing — that is the whole point of Responding — so there is no
 * countdown left to draw, and the card used to say `on it` and nothing else.
 * Which is true, and useless: the leader is asking how long this has been
 * open, and the group that most often holds the answer was the one with no
 * number on it at all. Same for a Safety broadcast, which bypasses the ladder
 * and never had one.
 *
 * A minus sign rather than the word OVERDUE, for the breached case. A card
 * that says "OVERDUE 0:47" beside one that says "7:41" makes the reader
 * parse two formats to work out which is worse.
 */
export function floorClock(a: AndonAlertVM, now: number, agoWord: string): { text: string; color: string } {
  // No countdown running: neither engaged nor bypassing means there is
  // nothing to run out, so the honest number is the age.
  const age = { text: ago(Math.max(0, now - a.raisedAt), agoWord), color: 'var(--sub)' };
  if (a.sla.phase === 'responding') return age;
  if (a.sla.msLeft == null) return age;
  const left = a.sla.dueAt == null ? a.sla.msLeft : a.sla.dueAt - now;
  if (left <= 0) return { text: `−${clock(-left)}`, color: 'var(--danger)' };
  return { text: clock(left), color: left < 60_000 ? 'var(--warn)' : 'var(--ink)' };
}

/**
 * The chip on the card's face.
 *
 * A stop forecast that has come due unconfirmed is a state a plain stage
 * grouping cannot show at all. It stays in its proper group and wears a chip
 * rather than bending the stage model into a group that means "attention".
 *
 * Ordered by what changes behaviour first, and only one is ever shown: a card
 * with three badges on it is a card nobody reads.
 */
export interface Badge {
  text: StringKey;
  tone: 'danger' | 'warn' | 'pos' | 'quiet';
}

export function badgeOf(a: AndonAlertVM): Badge | null {
  if (a.stopForecast?.due) return { text: 'badgeDidItStopT', tone: 'warn' };
  if (a.needsStopEstimate) return { text: 'badgeWhenWillItStopT', tone: 'warn' };
  if (a.owner.unowned && a.open) return { text: 'badgeNoOwnerT', tone: 'danger' };
  if (a.fixEta?.overdue) return { text: 'badgeFixRunningOverT', tone: 'warn' };
  if (a.responding) return { text: 'badgeRespondingT', tone: 'warn' };
  // A closed andon is closed: nobody is on the way to it. `acked` survives
  // the close on the record, so the stage is checked first.
  if (!a.open) return null;
  if (a.acked) return { text: 'badgeOnTheWayT', tone: 'warn' };
  return null;
}
